<template>
  <div class="grid grid-cols-12">
    <div class="col-span-3 p-8 pt-16 lg:p-12 lg:pl-14 bg-transaction-gradient
                space-y-12">
      <ValueInput v-model.trim="$v.form.sum.$model"
                  :label="$t('sum')"
                  :currency_id="!!form.account ? form.account.currency.id : null"
                  :class="{'--error':$v.form.sum.$error}">
        <template v-slot:errors="">
          <div class="error" v-if="$v.form.sum.$invalid">{{ $t('fieldRequired') }}</div>
        </template>
      </ValueInput>
    </div>
    <div class="col-span-9
                p-8 pt-16 lg:p-12 lg:pr-17
                grid grid-cols-2 col-gap-12 row-gap-12">
      <AccountsSelect v-model="form.account"
                      :label="$t('accountLabel')"
                      hidden-mode="keep-selected"
                      :accounts="accounts.data"
                      :account_groups="accounts_by_groups"
                      :grouped="accounts_by_groups.length > 1"
                      @open="open_form = true"/>
      <div class="col-span-2 grid grid-cols-2 col-gap-12">
        <VueCtkDateTimePicker v-model="form.date"
                              id="transaction-date"
                              format="YYYY-MM-DD"
                              formatted="DD.MM.YYYY"
                              :locale="$i18n.locale"
                              :label="$t('dateLabel')"
                              :auto-close="true"
                              :only-date="true"
                              :no-clear-button="true"
                              :no-button="true"
                              color="#2D4C5E"/>
        <VueCtkDateTimePicker v-if="current_space.data.time_enabled"
                              id="transaction-time"
                              v-model="form.time"
                              format="HH:mm"
                              formatted="HH:mm"
                              :locale="$i18n.locale"
                              :label="$t('timeLabel')"
                              :only-time="true"
                              :no-clear-button="true"
                              :no-button="true"
                              color="#2D4C5E"/>
      </div>
      <BaseCheckbox :checked="form.off_report"
                    size="sm"
                    class="-mt-5"
                    @change="form.off_report = !form.off_report">
        <span class="text-sm">{{ $t('isReportText') }}</span>
      </BaseCheckbox>
      <BaseCheckbox :checked="form.off_budget"
                    size="sm"
                    class="-mt-5"
                    @change="form.off_budget = !form.off_budget">
        <span class="text-sm">{{ $t('isBudgetText') }}</span>
      </BaseCheckbox>
      <div class="col-span-2 flex items-center">
        <BaseButton @click="saveChanges"
                    class="mr-8"
                    style="width: 255px;"
                    :loading="submitted"
                    theme="gray">
          {{ $t('saveChanges') }}
        </BaseButton>
        <BaseButton @click="$emit('cancel')"
                    style="width: 140px;"
                    :loading="submitted"
                    theme="white">
          {{ $t('cancel') }}
        </BaseButton>
        <ArchiveBtn class="ml-auto"
                    @confirm="removeOperation"
                    :submitted="submitted">
          <template v-slot:text>
            {{ $t('remove') }}
          </template>
        </ArchiveBtn>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {decimal, required, requiredIf} from "vuelidate/lib/validators";
import ValueInput from "@/components/UI/BaseInput/ValueInput";
import AccountsSelect from "@/components/UI/BaseSelect/AccountsSelect";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseCheckbox from "@/components/UI/BaseCheckbox";
import IconTrash from "@/components/icons/IconTrash";
import TransactionEditModalFormRemoveButton
  from "@/components/common/Transaction/TransactionEditModal/TransactionEditModalFormRemoveButton";
import ArchiveBtn from "@/components/UI/ArchiveBtn";

export default {
  name: "TransactionEditFormSystem",
  components: {
    ArchiveBtn,
    TransactionEditModalFormRemoveButton,
    IconTrash, BaseCheckbox, BaseButton, BaseSelect, AccountsSelect, ValueInput
  },
  props: {
    transaction_item: {
      required: true
    },
    transaction_options: {
      required: true
    },
    submitted: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      form: {
        sum: null,
        account: null,
        date: null,
        time: null,
        off_report: false,
        off_budget: false
      }
    }
  },
  validations: {
    form: {
      sum: {
        required,
        decimal,
      },
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/expenses', ['expenses']),
    ...mapState('space/incomes', ['incomes']),
    ...mapState('space/tags', ['tags_list']),
    ...mapState('space/accounts', ['accounts']),
    ...mapGetters('space/accounts', ['accounts_by_groups']),
    ...mapGetters('space/expenses', ['expenses_by_groups']),
  },
  methods: {
    ...mapActions('space/tags', ['createTag']),
    addTag(val) {
      const tag = {title: val, space_id: this.current_space.data.id};
      this.createTag(tag).then(res => {
        if (res.body.success) {
          this.form.tags.push(res.body.data);
          this.$store.commit('space/tags/pushTag', res.body.data);
        }
      })
    },
    setFields(transaction) {
      this.form.sum = transaction.sum;
      this.form.sum = this.form.sum.toFixed(transaction.account.currency.decimals)
      this.form.account = transaction.account;
      this.form.off_report = transaction.off_report;
      this.form.off_budget = transaction.off_budget;

      this.form.date = this.$moment.unix(transaction.date).format('YYYY-MM-DD');
      this.form.time = this.$moment.unix(transaction.date).format('HH:mm');
    },
    prepareData(payload) {
      let data = {}

      data.id = this.transaction_item.id;
      data.account_id = payload.account.id;
      data.sum = +payload.sum;

      data.date_time = this.current_space.data.time_enabled ?
          `${payload.date} ${payload.time}:${this.$moment().format('ss')}` :
          `${payload.date} ${this.$moment().format('HH:mm:ss')}`;
      data.timezone = this.$moment.tz.guess();
      data.off_report = payload.off_report;
      data.off_budget = payload.off_budget;

      return data;
    },
    saveChanges() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const data = this.prepareData(this.form);
        this.$emit('updateTransaction', data)
      }
    },
    removeOperation() {
      this.$emit('removeTransaction', this.transaction_item.id)
    }
  },
  watch: {
    transaction_item: {
      immediate: true,
      handler(newVal) {
        if (!!newVal) {
          this.setFields(newVal)
        }
      }
    }
  },
  i18n: {
    messages: {
      en: {
        fieldRequired: 'Field is required',
        sum: 'Sum',
        accountLabel: 'Account',
        dateLabel: 'Transaction date',
        timeLabel: 'Transaction time',
        isReportText: 'ignore in reports',
        isBudgetText: 'ignore in budgets',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        remove: 'Delete',
      },
      ru: {
        fieldRequired: 'Поле обязательно',
        sum: 'Сумма',
        accountLabel: 'Счет',
        dateLabel: 'Дата операции',
        timeLabel: 'Время операции',
        isReportText: 'не учитывать сумму в отчетах',
        isBudgetText: 'не учитывать сумму в бюджетах',
        saveChanges: 'Сохранить изменения',
        cancel: 'Отмена',
        remove: 'Удалить',
      }
    }
  }
}
</script>